import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from '@mui/icons-material';
import {
  Autocomplete,
  TextField,
  InputAdornment,
  Box,
  styled,
  Chip,
} from '@mui/material';

import { customAutocompleteStyles } from '../CustomAutocomplete';

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.text.primary,
  fontWeight: 'bold',
  backgroundColor: theme.palette.background.light,
}));

const GroupItems = styled('ul')({
  padding: 0,
});

const GlobalSearch = ({
  list,
  loading,
  onSelected,
  externalFilter,
  external,
  multiple,
  noAdornment,
  label,
}) => {
  const { t } = useTranslation();
  const [filterText, setFilterText] = useState(multiple ? [] : null);
  const state = external ? externalFilter : filterText;

  return (
    <Box sx={customAutocompleteStyles}>
      <Autocomplete
        id="global-search"
        data-testid="global-search"
        loading={loading}
        options={list}
        multiple={multiple}
        groupBy={option => option.type}
        getOptionLabel={option => option?.label}
        value={state}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={(_event, opt) => {
          !external && setFilterText(opt);
          onSelected(opt);
        }}
        noOptionsText={t('components.autocomplete.empty_list')}
        includeInputInList
        renderGroup={params => (
          <li key={params.key}>
            <GroupHeader>{t(`atoms.globalsearch.${params.group}`)}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )}
        renderInput={params => {
          const noTags = multiple ? (state?.length ?? 0) === 0 : !state;
          return (
            <TextField
              {...params}
              data-testid="autocomplete-input"
              placeholder={
                multiple && state?.length > 0
                  ? ''
                  : label ?? t('atoms.globalsearch.label')
              }
              fullWidth
              variant="standard"
              InputProps={{
                ...params.InputProps,
                sx: {
                  pl: noAdornment && noTags ? 1 : 0, // MUI spacing(2) = 16px
                },
                startAdornment: noAdornment ? (
                  params.InputProps.startAdornment
                ) : (
                  <InputAdornment
                    position="start"
                    sx={{ marginRight: '5px', marginLeft: '5px' }}
                  >
                    <Search />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                ...params.InputLabelProps,
                sx: {
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  width: '100%',
                  color: 'green',
                },
              }}
            />
          );
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              key={option.value}
              label={option.label}
              color="primary"
              sx={{ color: 'white', height: '1.8rem' }}
              {...getTagProps({ index })}
            />
          ))
        }
      />
    </Box>
  );
};

GlobalSearch.defaultProps = {
  loading: false,
  external: false,
  multiple: false,
  noAdornment: false,
  label: null,
};

GlobalSearch.propTypes = {
  onSelected: PropTypes.func.isRequired,
  list: PropTypes.array.isRequired,
  externalFilter: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  loading: PropTypes.bool,
  external: PropTypes.bool,
  multiple: PropTypes.bool,
  noAdornment: PropTypes.bool,
  label: PropTypes.string,
};

export default GlobalSearch;
